/**
 * When the next blood work is due, and whether today is a day to say so.
 *
 * Every other reminder in this app fires from a fixed daily or weekly time. Blood work is
 * different: there is no "every Tuesday" for a lab draw, only "some months after the last
 * one" or a date chosen by hand before any draw has ever been logged. This module resolves
 * that single due date and answers the one question the reminder policy needs of it — how
 * many days away is it — leaving the day-of policy (what time, what the message says) to
 * `shared/domain/reminders/policy.js`, the same as every other reminder type.
 */

import { addDateKeyDays, assertDateKey } from '../../time/date-key.js';

export const BLOODWORK_INTERVAL_UNITS = Object.freeze(['weeks', 'months']);
export const DEFAULT_BLOODWORK_INTERVAL = Object.freeze({ unit: 'months', value: 2 });

/** How many days out a reminder fires: three heads-up days, then the day itself. */
export const BLOODWORK_REMINDER_LEAD_DAYS = Object.freeze([3, 2, 1, 0]);

function pad2(value) {
  return String(value).padStart(2, '0');
}

/** Last day of a month, `month` 1-based. Day 0 of the next month is the last of this one. */
function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Add whole calendar months to a date key, clamping into a short month rather than
 * overflowing into the next one — the same rule `monthlyDateKeys` uses for appointments,
 * because "two months after the 31st" meaning "the 30th of the following-following month"
 * silently skips a month and drifts every cycle after it.
 *
 * @param {string} dateKey
 * @param {number} months
 * @returns {string}
 */
function addCalendarMonths(dateKey, months) {
  assertDateKey(dateKey);
  const year = Number(dateKey.slice(0, 4));
  const month = Number(dateKey.slice(5, 7));
  const day = Number(dateKey.slice(8, 10));
  const absolute = month - 1 + months;
  const targetYear = year + Math.floor(absolute / 12);
  const targetMonth = ((absolute % 12) + 12) % 12 + 1;
  return `${targetYear}-${pad2(targetMonth)}-${pad2(Math.min(day, lastDayOfMonth(targetYear, targetMonth)))}`;
}

/**
 * Add an interval, in whichever unit it is stored, to a date key.
 *
 * @param {string} dateKey
 * @param {{unit: 'weeks'|'months', value: number}} interval
 * @returns {string}
 */
export function addInterval(dateKey, { unit, value }) {
  return unit === 'weeks' ? addDateKeyDays(dateKey, value * 7) : addCalendarMonths(dateKey, value);
}

/**
 * The date the next blood work is due.
 *
 * Once there is a logged draw, the due date always rolls forward from it — that is what
 * "every 2 months" means once you are actually on the cadence, and it is why this ignores
 * `manualDueDate` the moment a draw exists rather than averaging the two. `manualDueDate` is
 * only for the gap before a first draw has ever been logged, when there is nothing to roll
 * forward from; a profile with neither falls back to "the interval from today", so a due
 * date always exists rather than the reminder silently never firing.
 *
 * @param {{lastDrawDateKey: string|null, manualDueDate: string|null,
 *   interval: {unit: 'weeks'|'months', value: number}, todayDateKey: string}} input
 * @returns {string}
 */
export function resolveBloodworkDueDate({ lastDrawDateKey, manualDueDate, interval, todayDateKey }) {
  if (lastDrawDateKey) return addInterval(lastDrawDateKey, interval);
  if (manualDueDate) return manualDueDate;
  return addInterval(todayDateKey, interval);
}

/**
 * Days from today until the due date. Negative once it has passed, so a reminder that
 * fires "on or after" a lead day is a single `<=` comparison rather than a special case for
 * overdue.
 *
 * @param {string} dueDateKey
 * @param {string} todayDateKey
 * @returns {number}
 */
export function daysUntilBloodworkDue(dueDateKey, todayDateKey) {
  // `Date.UTC` takes a zero-based month; a date key's month is one-based. Feeding "08"
  // straight in reads August as September, and for the 31st of a real 31-day month that
  // overflows into the 1st of the month after, since the mislabelled month may only have
  // 30 days — a wrong answer only on that one day of the year otherwise easy to miss.
  const toUtcMs = (key) => { const [year, month, day] = key.split('-').map(Number); return Date.UTC(year, month - 1, day); };
  return Math.round((toUtcMs(dueDateKey) - toUtcMs(todayDateKey)) / 86_400_000);
}

/**
 * Whether today is one of the lead days: three days out, two, one, or the day itself.
 * Also true for any day the reminder was somehow never seen (overdue), so a missed window
 * does not mean blood work silently stops being asked for.
 *
 * @param {number} daysUntil
 * @returns {boolean}
 */
export function isBloodworkReminderDue(daysUntil) {
  return daysUntil <= BLOODWORK_REMINDER_LEAD_DAYS[0];
}
